import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../SessionManagement/AuthContext";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Snackbar,
  Alert,
  Backdrop,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { xorDecrypt } from "../../encrypt";
import useSharedLink from "../../SharedLink";
import axiosInstance from "../../api/axiosInstance";
import startImg from "../../assets/img/starter.png";
import professionalImg from "../../assets/img/professional.png";
import businessImg from "../../assets/img/business.png";
import EmailModal from "../EmailModal";
import pageHeaderImage from "../../NavbarS/img_and_css/img/page_header_1.jpg";
import pageFooterLogoImage from "../../NavbarS/img_and_css/img/logo.png";
import pageFooterBgImage from "../../NavbarS/img_and_css/img/footer_bg.jpeg";

const Subscription = () => {
  const { state } = useContext(AuthContext);
  const isAuthenticated =
    state.isAuthenticated || xorDecrypt(localStorage.getItem("username"));
  const navigate = useNavigate();
  const [prices, setPrices] = useState({
    bcbaPremiumPlan: "",
    rbtPremiumPlan: "",
    rbtBasicPlan: "",
    bcbaPlusPlan: "",
    bcbaBasicPlan: "",
    rbtPlusPlan: "",
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [countdownSnackbarOpen, setCountdownSnackbarOpen] = useState(false);
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [pleaseWaitSnackbarOpen, setPleaseWaitSnackbarOpen] = useState(false);
  const sharedLink = useSharedLink();
  const [activeTab, setActiveTab] = useState(0);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [tabTransition, setTabTransition] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleTabClick = (tabId) => {
    if (tabId !== activeTab) {
      setTabTransition(true);
      setTimeout(() => {
        setActiveTab(tabId);
        setTabTransition(false);
      }, 300); // Duration of the slide animation
    }
  };

  useEffect(() => {
    axiosInstance
      .get(`${sharedLink}/config`, {
        withCredentials: true,
      })
      .then((response) => {
        const {
          "BCBA Premium Plan": bcbaPremiumPlan,
          "RBT Premium Plan": rbtPremiumPlan,
          "RBT Basic Plan": rbtBasicPlan,
          "BCBA Plus Plan": bcbaPlusPlan,
          "BCBA Basic Plan": bcbaBasicPlan,
          "RBT Plus Plan": rbtPlusPlan,
        } = response.data;
        setPrices({
          bcbaPremiumPlan,
          rbtPremiumPlan,
          rbtBasicPlan,
          bcbaPlusPlan,
          bcbaBasicPlan,
          rbtPlusPlan,
        });
      })
      .catch((error) => {
        console.error("Error fetching config:", error);
      });
  }, [sharedLink]);

  const handleCustomerPortalClick = async () => {
    setSnackbarMessage("Redirecting you to the customer portal...");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);

    try {
      const email = xorDecrypt(localStorage.getItem("username"));

      if (!email) {
        throw new Error("Email not found in local storage");
      }

      const response = await axiosInstance.get(
        `${sharedLink}/api/activeEnrollment?email=${email}`,
        {
          withCredentials: true,
        }
      );
      const customerID = response.data.customerId;
      if (!customerID) {
        throw new Error("Customer ID not found in the response");
      }

      // Open a new tab immediately
      const newTab = window.open("", "_blank");

      if (!newTab) {
        setSnackbarOpen(false);
        setSnackbarMessage("Popup blocked. Please allow popups and try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      const portalResponse = await axiosInstance.post(
        `${sharedLink}/customer-portal`,
        null,
        {
          withCredentials: true,
          params: {
            customer: customerID,
          },
          responseType: "json",
        }
      );

      // Set the location of the new tab after the response is received
      newTab.location.href = portalResponse.data.redirectUrl;
    } catch (error) {
      console.error("Error occurred:", error);
      setSnackbarOpen(false);
      setSnackbarMessage("An error occurred. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
    setSnackbarOpen(false);
  };

  const handleChoosePlan = (priceId) => {
    const encryptedEnrollmentStatus = localStorage.getItem("enrollmentStatus");
    const enrollmentStatus = encryptedEnrollmentStatus
      ? xorDecrypt(encryptedEnrollmentStatus)
      : null;

    if (enrollmentStatus === "true") {
      const encryptedEnrollmentEndDate =
        localStorage.getItem("enrollmentEndDate");
      const enrollmentEndDate = encryptedEnrollmentEndDate
        ? new Date(xorDecrypt(encryptedEnrollmentEndDate))
        : null;

      if (enrollmentEndDate) {
        const now = new Date();
        const differenceInDays =
          (now - enrollmentEndDate) / (1000 * 60 * 60 * 24);

        if (differenceInDays <= 7) {
          setSelectedPlan(priceId);
          setConfirmationOpen(true);
          setTermsChecked(false);
          
          return;
        }
      }

      handleCustomerPortalClick();
      return;
    }

    setSelectedPlan(priceId);
    setConfirmationOpen(true);
    setTermsChecked(false);
  };

  const handleConfirmPlan = () => {
    localStorage.removeItem("transactionStored");
    if (!isAuthenticated) {
      setSnackbarOpen(true);
      setBackdropOpen(true);
      return;
    }
    if (!termsChecked) return;
    setConfirmationOpen(false);
    setPleaseWaitSnackbarOpen(true);
    const email = xorDecrypt(localStorage.getItem("username"));
    axiosInstance
      .post(
        `${sharedLink}/create-checkout-session`,
        { priceId: selectedPlan, email },
        {
          withCredentials: true,
        }
      )
      .then((response) => {
        window.location.href = response.data.url;
      })
      .catch((error) => {
        console.error("Error creating checkout session:", error);
        setPleaseWaitSnackbarOpen(false);
        document.getElementById("error-message").innerText =
          "Error creating checkout session. Please try again.";
      });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("Please login to proceed. Redirecting to login page...");
    setSnackbarSeverity("info");
    setCountdownSnackbarOpen(true);
    const interval = setInterval(() => {
      setCountdown((prevCountdown) => {
        if (prevCountdown === 1) {
          clearInterval(interval);
          setBackdropOpen(false);
          navigate("/login");
        }
        return prevCountdown - 1;
      });
    }, 1000);
  };

  const handleCountdownSnackbarClose = () => {
    setCountdownSnackbarOpen(false);
    setBackdropOpen(false);
  };

  const plans = [
    {
      title: "Basic",
      details: {
        "BT/RBT": [
          "Unlimited Clients",
          "Therapist Notes (97153)",
          "Upto 30 Credits (Max Notes generation allowed 30 per month)",
          "Session Notes Secure Storage for One year",
        ],
        BCBA: [
          "Unlimited Clients",
          "Assessment Notes (97151), Therapist Notes (97153), Supervision (97155), Parent training (97156)",
          "Upto 30 Credits (Max Notes generation allowed 30 per month)",
          "Session Notes Secure Storage for One year",
        ],
      },
      prices: ["$29.99 / month", "$49.99 / month"],
      priceIds: [prices.rbtBasicPlan, prices.bcbaBasicPlan],
      disabled: false,
    },
    {
      title: "Plus",
      details: {
        "BT/RBT": [
          "Unlimited Clients",
          "Therapist Notes (97153)",
          "Upto 60 Credits (Max Notes generation allowed 60 per month)",
          "Session Notes Secure Storage for One year",
        ],
        BCBA: [
          "Unlimited Clients",
          "Assessment Notes (97151), Therapist Notes (97153), Supervision (97155), Parent training (97156)",
          "Upto 60 Credits (Max Notes generation allowed 60 per month)",
          "Session Notes Secure Storage for One year",
        ],
      },
      prices: ["$49.99 / month", "$89.99 / month"],
      priceIds: [prices.rbtPlusPlan, prices.bcbaPlusPlan],
      disabled: false,
    },
    {
      title: "Premium",
      details: {
        "BT/RBT": [
          "Unlimited Clients",
          "Therapist Notes (97153)",
          "Upto 500 Credits (Max Notes generation allowed 500 per month)",
          "Session Notes Secure Storage for One year",
        ],
        BCBA: [
          "Unlimited Clients",
          "Assessment Notes (97151), Therapist Notes (97153), Supervision (97155), Parent training (97156), Treatment plan documentation (H0032)",
          "Upto 500 Credits (Max Notes generation allowed 500 per month)",
          "Session Notes Secure Storage for One year",
        ],
      },
      prices: ["$79.99 / month", "$129.99 / month"],
      priceIds: [prices.rbtPremiumPlan, prices.bcbaPremiumPlan],
      disabled: false,
    },
  ];

  return (
    <Container
      style={{
        width: "100vw",
        height: "100vh",
        padding: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <header
        className="cs_page_header position-relative background-filled d-flex align-items-center justify-content-center"
        style={{
          backgroundImage: `url(${pageHeaderImage})`,
          backgroundColor: "#000", // Fallback background color
          width: "100vw",
          height: "45vh", // Ensure the section has height
        }}
      >
        <div className="container position-relative z-index-1 text-left">
          <h1 className="cs_fs_48 cs_fs_lg_36 text-white m-0">SUBSCRIPTION</h1>
        </div>
        <div className="position-absolute end-0 bottom-0">
          <svg
            width="660"
            height="497"
            viewBox="0 0 660 497"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M240 0H660L430 497H0L240 0Z"
              fill="url(#paint0_linear_81_9510)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_81_9510"
                x1="330"
                y1="78.2497"
                x2="375.052"
                y2="780.743"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="white" stopOpacity="0" offset="0" />
                <stop offset="0.9999" stopColor="#D9D9D9" stopOpacity="0.35" />
                <stop offset="1" stopColor="#222121" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </header>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          width: "100%",
        }}
      >
        <label>
          <input
            type="checkbox"
            checked={activeTab === 1}
            onChange={() => handleTabClick(activeTab === 0 ? 1 : 0)}
          />
          <div className="base-color">
            <div className="toggle-slider"></div>
            <span className="cash">BT/RBT</span>
            <span className="token">BCBA</span>
          </div>
        </label>

        <Grid
          container
          spacing={3}
          className="plans-container"
          justifyContent="center"
          alignItems="center"
        >
          {plans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card className={`plan-card ${plan.disabled ? "disabled" : ""}`}>
                <CardContent>
                  <Typography
                    variant="h5"
                    component="div"
                    className="plan-title"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {plan.title}
                  </Typography>
                  <Typography
                    variant="h6"
                    component="div"
                    className="plan-price"
                    style={{
                      color: "#e9a132",
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: "600",
                    }}
                  >
                    {plan.prices[activeTab]}
                  </Typography>
                  <div className="plan-description">
                    {plan.details[activeTab === 0 ? "BT/RBT" : "BCBA"].map(
                      (line, idx) => (
                        <p key={idx}>{line}</p>
                      )
                    )}
                  </div>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={plan.disabled}
                    className="choose-plan-btn"
                    style={{
                      color: plan.disabled ? "#ffffff" : "#121212", // Set text color to white when disabled
                    }}
                    onClick={() => handleChoosePlan(plan.priceIds[activeTab])}
                  >
                    {plan.disabled ? "Upcoming" : "Choose Plan"}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </div>

      <footer
        className="cs_footer background-filled text-white"
        style={{
          backgroundImage: `url(${pageFooterBgImage})`,
          width: "100vw",
          marginTop: "20px",
        }}
      >
        <div className="container">
          <div className="cs_footer_top d-flex justify-content-between align-items-start align-items-md-center cs_pt_46 cs_pb_46 cs_column_gap_15 cs_row_gap_15 flex-md-row flex-column">
            <div className="cs_footer_contact_info">
              <div
                className="cs_footer_logo wow zoomIn"
                data-wow-duration="0.8s"
                data-wow-delay="0.2s"
              >
                <img src={pageFooterLogoImage} alt="InSight ABA Notes" />
              </div>
            </div>

            <div className="cs_footer_contact_info">
              <h3 className="text-accent fw-normal cs_mb_4 cs_fs_16 cs_lh_lg">
                Send Email
              </h3>
              <h2 className="text-white fw-medium m-0 cs_fs_22">
                <a
                  href="mailto:Support@insightabanotes.com"
                  style={{ textDecoration: "none", color: "inherit" }}
                  onMouseEnter={(e) => (e.target.style.color = "#e9a132")}
                  onMouseLeave={(e) => (e.target.style.color = "inherit")}
                >
                  Support@insightabanotes.com
                </a>
              </h2>
            </div>
          </div>
          <div className="cs_footer_main cs_pt_30 cs_pb_30">
            <div className="row">
              <div className="col-lg-4">
                <div className="cs_footer_item cs_pt_20 cs_pb_20">
                  <div className="cs_text_widget">
                    <p>
                      Applied Behavior Analysis (ABA) notes are detailed records
                      used by behavior analysts to track and analyze the
                      progress of clients undergoing ABA therapy.
                    </p>
                  </div>
                  <div className="cs_social_btns d-flex flex-wrap cs_column_gap_15 cs_row_gap_15 cs_transition_5">
                    <a
                      href="https://www.facebook.com/insightabanotes/"
                      className="d-flex align-items-center justify-content-center cs_height_35 cs_width_35 text-white rounded-circle"
                    >
                      <i className="fa-brands fa-facebook-f"></i>
                    </a>
                    <a
                      href="https://x.com/insightabanotes"
                      className="d-flex align-items-center justify-content-center cs_height_35 cs_width_35 text-white rounded-circle"
                    >
                      <i className="fa-brands fa-x"></i>
                    </a>
                    <a
                      href="https://www.linkedin.com/in/insight-aba-notes-b92840348/"
                      className="d-flex align-items-center justify-content-center cs_height_35 cs_width_35 text-white rounded-circle"
                    >
                      <i className="fa-brands fa-linkedin-in"></i>
                    </a>
                    <a
                      href="https://www.youtube.com/@InSightABANotes"
                      className="d-flex align-items-center justify-content-center cs_height_35 cs_width_35 text-white rounded-circle"
                    >
                      <i className="fa-brands fa-youtube"></i>
                    </a>
                  </div>
                </div>
              </div>
              <div className="col-lg-4 offset-lg-1">
                <div className="cs_footer_item cs_pt_20 cs_pb_20">
                  <h2 className="cs_widget_title text-white cs_fs_22 cs_mb_22">
                    Explore
                  </h2>
                  <p>
                    <a
                      href="/about-us"
                      style={{ textDecoration: "none", color: "inherit" }}
                      onMouseEnter={(e) => (e.target.style.color = "#e9a132")}
                      onMouseLeave={(e) => (e.target.style.color = "inherit")}
                    >
                      About us
                    </a>
                  </p>
                  <p>
                    <a
                      href="privacypolicy"
                      style={{ textDecoration: "none", color: "white" }}
                      onMouseEnter={(e) => (e.target.style.color = "#e9a132")}
                      onMouseLeave={(e) => (e.target.style.color = "white")}
                    >
                      Privacy Policy
                    </a>
                  </p>
                  <p>
                    <a
                      href="termsandconditions"
                      style={{ textDecoration: "none", color: "inherit" }}
                      onMouseEnter={(e) => (e.target.style.color = "#e9a132")}
                      onMouseLeave={(e) => (e.target.style.color = "inherit")}
                    >
                      Terms & Conditions
                    </a>
                  </p>
                </div>
              </div>
              <div className="col-lg-3">
                <div className="cs_footer_item cs_pt_20 cs_pb_20">
                  <div className="cs_newsletter cs_style_1">
                    <h2 className="cs_widget_title text-white cs_fs_22 cs_mb_22">
                      Explore
                    </h2>
                    <p>
                      <a
                        href="demographics"
                        style={{ textDecoration: "none", color: "inherit" }}
                        onMouseEnter={(e) => (e.target.style.color = "#e9a132")}
                        onMouseLeave={(e) => (e.target.style.color = "inherit")}
                      >
                        Demo
                      </a>
                    </p>
                    <p>
                      <a
                        href="services"
                        style={{ textDecoration: "none", color: "inherit" }}
                        onMouseEnter={(e) => (e.target.style.color = "#e9a132")}
                        onMouseLeave={(e) => (e.target.style.color = "inherit")}
                      >
                        Services
                      </a>
                    </p>
                    <p>
                      <a
                        href="contact"
                        style={{ textDecoration: "none", color: "inherit" }}
                        onMouseEnter={(e) => (e.target.style.color = "#e9a132")}
                        onMouseLeave={(e) => (e.target.style.color = "inherit")}
                      >
                        Contact
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="cs_copyright text-center cs_fs_124 cs_lh_lg cs_pt_36 cs_pb_36">
          <div className="container">
            <p className="m-0">
              Copyright © 2025 <a href="/#">InSight ABA NOTES</a>. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>
      <Dialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#000",
            color: "#fff",
            padding: "20px",
            borderRadius: "10px",
          },
        }}
      >
        <DialogTitle sx={{ color: "#e9a132" }}>Terms & Conditions</DialogTitle>
        <DialogContent sx={{ maxHeight: "60vh", overflowY: "auto" }}>
          <p>
            1. AI-Generated Notes Disclaimer: The session notes are
            AI-generated. Please review and verify them before using.
          </p>
          <p>
            2. Credit Usage Policy: Credits are based on API calls and cannot be
            refunded or transferred.
          </p>
          <p>
            3. Plan Upgrade Policy: Unused credits from previous plans do not
            roll over to new plans.
          </p>
          <p>
            4. Liability Disclaimer: We are not liable for inaccuracies or
            misuse of AI-generated session notes.
          </p>
        </DialogContent>
        <DialogActions>
          <FormControlLabel
            control={
              <Checkbox
                sx={{ color: "#e9a132" }}
                checked={termsChecked}
                onChange={(e) => setTermsChecked(e.target.checked)}
              />
            }
            label="I agree to the Terms & Conditions"
          />
          <Button
            onClick={() => setConfirmationOpen(false)}
            sx={{ backgroundColor: "#333", color: "#fff" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmPlan}
            disabled={!termsChecked}
            sx={{
              backgroundColor: termsChecked ? "#e9a132" : "#555",
              color: "#000",
            }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={pleaseWaitSnackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="info">Please wait...</Alert>
      </Snackbar>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={countdownSnackbarOpen}
        autoHideDuration={3000}
        onClose={handleCountdownSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCountdownSnackbarClose} severity="error">
          Redirecting you to the login page in {countdown}...
        </Alert>
      </Snackbar>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={backdropOpen}
      />
      <style>{`
        label {
    position: relative;
    display: block;
    width: 300px;
    height: 50px;
    margin: 35px auto;
    cursor: default;
    text-transform: uppercase;
  }

  label * {
    transition: all 200ms linear;
  }

  label input[type="checkbox"] {
    opacity: 0;
    position: absolute;
    width: 100%;
    height: 100%;
  }

  label input[type="checkbox"]:checked + .base-color .toggle-slider {
    margin-left: 43.5%;
  }

  label input[type="checkbox"]:checked + .base-color .cash {
    color: #000;
  }

  label input[type="checkbox"]:checked + .base-color .token {
    color: #e9a132;
  }

  label input[type="checkbox"] + .base-color {
    position: absolute;
    width: 100%;
    height: 100%;
    transition: all 150ms linear;
    background: #e9a132;
    border-radius: 32px;
  }

  label input[type="checkbox"] + .base-color .toggle-slider {
    display: block;
    position: absolute;
    content: " ";
    background: #1e1e1e;
    width: 55%;
    height: 100%;
    transition: margin 300ms linear;
    border-radius: 32px;
    height: 46px;
    top: 2px;
    left: 2px;
  }

  label input[type="checkbox"] + .base-color .cash {
    color: #e9a132;
  }

  label .cash,
  label .token {
    display: block;
    position: absolute;
    width: 50%;
    color: #fff;
    line-height: 50px;
    text-align: center;
    font-weight: bold; /* Make text bold */
    font-family: 'Poppins', sans-serif; /* Use a better font */
     cursor: pointer;
  }

  label .cash {
    left: 0px;
  }

  label .token {
    right: 0px;
    color: #000;
  }

        .plans-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-top: 20px;
        }

        .plan-card {
          background-color: #1e1e1e; /* Keep the card background black */
          color: white;
          text-align: center;
          border-radius: 15px;
          padding: 30px;
          min-height: 500px;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .plan-card:hover {
          transform: scale(1.03); /* Adjusted to prevent scrollbar */
          box-shadow: 0 20px 50px rgba(255, 165, 0, 0.3);
        }

        .plan-card.disabled {
          opacity: 0.5;
          pointer-events: none;
          background-color: #333;
          border: 2px dashed #e9a132;
        }

        .plan-title {
          font-size: 1.8em;
          font-weight: 700;
          margin-bottom: 15px;
        }

        .plan-price {
          font-size: 1.4em;
          color: #e9a132;
          margin-bottom: 15px;
        }

        .plan-description {
          font-size: 1em;
          color: white;
          margin-bottom: 25px;
          text-align: left;
        }

        .choose-plan-btn {
          background-color: #e9a132;
          color: #121212;
          font-weight: 600;
          font-family: "Poppins", sans-serif;
          border-radius: 25px;
          padding: 12px 30px;
          transition: background-color 0.3s ease;
        }

        .choose-plan-btn:hover {
          background-color: #ffcc80;
          color: #121212;
        }

        @media (max-width: 768px) {
          .tabs {
            width: 90%;
          }

          .tab {
            font-size: 1rem;
            padding: 10px;
          }

          .plan-card {
            padding: 20px;
            height: auto;
          }

          .plan-title {
            font-size: 1.5em;
          }

          .plan-price {
            font-size: 1.2em;
          }

          .plan-description {
            font-size: 0.9em;
          }

          .choose-plan-btn {
            padding: 10px 20px;
          }
        }
      `}</style>
    </Container>
  );
};

export default Subscription;
